// 触发式腾讯云人机验证码（Web 版，对齐 App 的 runWithCaptcha 流程）。
//
// 登录默认不带 ticket 直接打；后端命中风控返回 AUTH.CAPTCHA_REQUIRED 时，
// 弹腾讯验证码拿到 "<randstr>:<ticket>"（后端约定格式）后带 ticket 重试一次。
// 用户取消则把原异常抛回，由调用方提示。
import { ApiError } from './client';

const CAPTCHA_APP_ID =
  (import.meta.env.VITE_CAPTCHA_APP_ID as string | undefined) ?? '198383751';

const SDK_URL = 'https://turing.captcha.qcloud.com/TCaptcha.js';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TencentCaptcha?: any;
  }
}

let sdkPromise: Promise<void> | null = null;

function loadSdk(): Promise<void> {
  if (window.TencentCaptcha) return Promise.resolve();
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SDK_URL;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        sdkPromise = null;
        reject(new Error('验证码组件加载失败，请检查网络后重试'));
      };
      document.head.appendChild(s);
    });
  }
  return sdkPromise;
}

/** 弹验证码；通过返回 "<randstr>:<ticket>"，用户取消/失败返回 null。 */
export async function showCaptcha(): Promise<string | null> {
  await loadSdk();
  return new Promise((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new window.TencentCaptcha(
      CAPTCHA_APP_ID,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (res: any) => {
        // res.ret: 0 验证成功, 2 用户主动关闭
        if (res && res.ret === 0 && res.ticket && res.randstr) {
          resolve(`${res.randstr}:${res.ticket}`);
        } else {
          resolve(null);
        }
      },
      {},
    );
  });
}

/**
 * 首次以 `undefined` ticket 调 action；命中 AUTH.CAPTCHA_REQUIRED 则弹验证码、
 * 带 ticket 重试一次。用户取消验证码 → rethrow 原异常。其它异常直接 rethrow。
 */
export async function runWithCaptcha<T>(
  action: (captchaTicket?: string) => Promise<T>,
): Promise<T> {
  try {
    return await action(undefined);
  } catch (e) {
    if (e instanceof ApiError && e.code === 'AUTH.CAPTCHA_REQUIRED') {
      const ticket = await showCaptcha();
      if (!ticket) throw e;
      return await action(ticket);
    }
    throw e;
  }
}
